import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { getReferralInfo, redeemRewardPoints } from '../../actions/userAction';
import './Referral.css';

// Reward values shown here are copy only — the real numbers live in
// projectMillionNew/server/utils/referralRewards.js (SIGNUP_POINTS,
// ACTIVATION_POINTS). Keep these in sync if those ever change.
const SIGNUP_POINTS = 25;
const ACTIVATION_POINTS = 125;

export default function Referral() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((s) => s.user);
  const referral = useSelector((s) => s.referral);
  const [shareState, setShareState] = useState('idle'); // 'idle' | 'copied' | 'shared'
  const [redeeming, setRedeeming] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState('');

  useEffect(() => {
    if (isAuthenticated) dispatch(getReferralInfo());
  }, [isAuthenticated, dispatch]);

  // window.location.origin (not config.apiUrl, that's the backend) so this
  // works correctly on both localhost and dmyor.com without extra config.
  const referralLink = referral.referralCode ? `${window.location.origin}/signup?ref=${referral.referralCode}` : '';

  const share = async () => {
    if (!referralLink) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join me on dmyor', url: referralLink });
        setShareState('shared');
      } catch (_) { return; } // user cancelled the native share sheet
    } else {
      try {
        await navigator.clipboard.writeText(referralLink);
        setShareState('copied');
      } catch (_) { return; }
    }
    setTimeout(() => setShareState('idle'), 2200);
  };

  const cap = referral.maxRewardedReferrals || 20;

  // Deliberately doesn't expose the exchange rate anywhere — only whether
  // anything was redeemed, never points spent or requests-per-point math.
  const redeem = async () => {
    if (redeeming || !referral.rewardPoints) return;
    setRedeeming(true);
    setRedeemMsg('');
    try {
      const data = await dispatch(redeemRewardPoints());
      setRedeemMsg(data.redeemed
        ? 'Redeemed! Bonus AI requests added to your account.'
        : "Not enough points yet — keep referring friends to earn more.");
    } catch (_) {
      setRedeemMsg('Could not redeem right now. Try again in a bit.');
    }
    setRedeeming(false);
    setTimeout(() => setRedeemMsg(''), 4500);
  };

  return (
    <div className="rp-page">
      <Helmet><title>Refer & earn · dmyor</title></Helmet>

      <div className="rp-wrap">
        <header className="rp-hero">
          <span className="rp-eyebrow">Referral program</span>
          <h1 className="rp-title">Invite friends, earn rewards</h1>
          <p className="rp-sub">
            Every person who joins dmyor from your link and starts their own vision earns you points —
            redeem them anytime for extra AI requests.
          </p>
        </header>

        <section className="rp-points-card">
          <div className="rp-points-row">
            <div>
              <div className="rp-points-num">{referral.rewardPoints || 0}</div>
              <div className="rp-points-label">reward points</div>
            </div>
            <button type="button" className="rp-redeem-btn" onClick={redeem} disabled={redeeming || !referral.rewardPoints}>
              {redeeming ? 'Redeeming…' : 'Redeem points'}
            </button>
          </div>
          {referral.aiBonusRequests > 0 && (
            <p className="rp-bonus-note">
              You've unlocked {referral.aiBonusRequests} bonus AI request{referral.aiBonusRequests === 1 ? '' : 's'}.
            </p>
          )}
          {redeemMsg && <p className="rp-redeem-msg">{redeemMsg}</p>}
        </section>

        <section className="rp-steps">
          <div className="rp-step">
            <span className="rp-step-num">1</span>
            <div>
              <p className="rp-step-title">Share your link</p>
              <p className="rp-step-desc">Send it to a friend, post it, or drop it anywhere.</p>
            </div>
          </div>
          <div className="rp-step">
            <span className="rp-step-num">2</span>
            <div>
              <p className="rp-step-title">They join dmyor</p>
              <p className="rp-step-desc">You earn <strong>{SIGNUP_POINTS} points</strong> the moment they sign up.</p>
            </div>
          </div>
          <div className="rp-step">
            <span className="rp-step-num">3</span>
            <div>
              <p className="rp-step-title">They create their first vision</p>
              <p className="rp-step-desc">You earn <strong>{ACTIVATION_POINTS} more points</strong> — {SIGNUP_POINTS + ACTIVATION_POINTS} total per friend.</p>
            </div>
          </div>
        </section>

        <section className="rp-link-card">
          <p className="rp-link-label">Your referral link</p>
          <div className="rp-link-row">
            <input
              className="rp-link-input"
              readOnly
              value={referralLink || 'Loading…'}
              onFocus={(e) => e.target.select()}
            />
            <button type="button" className="rp-share-btn" onClick={share} disabled={!referralLink}>
              {shareState === 'copied' ? 'Copied ✓' : shareState === 'shared' ? 'Shared ✓' : 'Copy link'}
            </button>
          </div>
          <p className="rp-link-hint">Up to {cap} rewarded referrals, lifetime. Points don't expire.</p>
        </section>
      </div>
    </div>
  );
}
